import { STANDALONE_DEFAULT_TEST_EMAIL } from './constants';

/**
 * Utilities for handling environment variables
 */

const SEEDED_EMAIL_STORAGE_KEY = 'billing.standalone.testEmail';

/**
 * Reads the `test-email` query parameter from the current URL. Returns
 * `null` outside a browser context or when the parameter is missing.
 *
 * @returns {string|null}
 */
function readTestEmailFromQuery() {
    if (typeof window === 'undefined' || !window.location) {
        return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('test-email') || null;
}

/**
 * Reads a previously-persisted test email from `sessionStorage`. Wrapped in
 * a try/catch so private-mode or quota errors cannot crash the app.
 *
 * @returns {string|null}
 */
function readPersistedTestEmail() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
    }
    try {
        return window.sessionStorage.getItem(SEEDED_EMAIL_STORAGE_KEY) || null;
    } catch (_err) {
        return null;
    }
}

/**
 * Persists the seeded test email to `sessionStorage`. Scoping to the tab
 * matches the "forget when the Docker stack is torn down" expectation: a
 * new tab after teardown starts unseeded.
 *
 * @param {string} email
 * @returns {void}
 */
function persistTestEmail(email) {
    if (typeof window === 'undefined' || !window.sessionStorage || !email) {
        return;
    }
    try {
        window.sessionStorage.setItem(SEEDED_EMAIL_STORAGE_KEY, email);
    } catch (_err) {
        // Private mode or quota errors are non-fatal — the app still works
        // against the URL query param, we just lose the soft-navigation latch.
    }
}

/**
 * Resolves the currently-active seeded test email. Prefers the URL query
 * parameter so fresh seeds always win, persists it to `sessionStorage` on
 * the way through, and falls back to the persisted value. This prevents
 * soft navigations (e.g. Dashboard's trial → `/plans` redirect) from
 * stripping the query and un-seeding the app.
 *
 * @returns {string|null}
 */
function resolveActiveTestEmail() {
    const queryEmail = readTestEmailFromQuery();
    if (queryEmail) {
        persistTestEmail(queryEmail);
        return queryEmail;
    }
    return readPersistedTestEmail();
}

/**
 * Clears any persisted standalone test email. Primarily for tests and
 * future "forget this user" flows — not called during normal seeding,
 * where a new URL simply overwrites the stored value.
 *
 * @returns {void}
 */
export function clearPersistedStandaloneTestEmail() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }
    try {
        window.sessionStorage.removeItem(SEEDED_EMAIL_STORAGE_KEY);
    } catch (_err) {
        // Intentionally swallow — no recovery action needed.
    }
}

/**
 * Converts an environment variable value to a boolean
 *
 * @param {string|undefined} value - The environment variable value to convert
 * @param {boolean} [defaultValue=false] - The default value if the env var is undefined or empty
 * @returns {boolean} The boolean value
 *
 * @example
 * envToBoolean('true') // returns true
 * envToBoolean('false') // returns false
 * envToBoolean('1') // returns true
 * envToBoolean('0') // returns false
 * envToBoolean(undefined) // returns false (default)
 * envToBoolean(undefined, true) // returns true (custom default)
 * envToBoolean('') // returns false (default for empty string)
 * envToBoolean('anything') // returns false (non-truthy values)
 */
export function envToBoolean(value, defaultValue = false) {
    // If value is undefined or null, return the default
    if (value === undefined || value === null) {
        return defaultValue;
    }

    // Ensure value is a string and trim whitespace
    const stringValue = String(value).trim();

    // If trimmed value is empty, return the default
    if (stringValue === '') {
        return defaultValue;
    }

    // Convert to lowercase for case-insensitive comparison
    const normalizedValue = stringValue.toLowerCase();

    // Check for truthy values
    // Supporting 'true', '1', 'yes', 'on' as truthy
    return (
        normalizedValue === 'true' ||
        normalizedValue === '1' ||
        normalizedValue === 'yes' ||
        normalizedValue === 'on'
    );
}

/**
 * Detects if the app is running in standalone mode (outside iframe) for testing/development
 *
 * @returns {boolean} True if running on port 8889 (standalone), false if running on port 8888 (iframe)
 *
 * @example
 * // In standalone mode (http://localhost:8889)
 * isStandaloneMode() // returns true
 *
 * // In iframe mode (http://localhost:8888)
 * isStandaloneMode() // returns false
 */
export function isStandaloneMode() {
    // Only in browser environment
    if (typeof window === 'undefined') {
        return false;
    }

    return window.location.port === '8889';
}

/**
 * Detects standalone mode before any scenario user has been seeded.
 *
 * A seeded test email is considered present when either the current URL
 * carries `?test-email=` or a previously-resolved email has been latched
 * into `sessionStorage`. The `sessionStorage` latch is what keeps the app
 * seeded across soft navigations that strip the query — notably the
 * Dashboard's trial-user redirect to `/plans`, which triggered an
 * unintended bounce back to the unseeded placeholder before this fix.
 *
 * @returns {boolean} True when in standalone mode and no seeded email is known.
 */
export function isUnseededStandalone() {
    if (!isStandaloneMode()) {
        return false;
    }
    return !resolveActiveTestEmail();
}

/**
 * Get test email with proper priority chain
 *
 * @private
 * @param {string} [email] - Email address to use. If provided, returns immediately
 * @returns {string} Email address from parameter, query param, env var, or default
 *
 * Priority chain:
 * 1. Function parameter
 * 2. URL query parameter `?test-email=...` (also latches into sessionStorage)
 * 3. Previously-latched `sessionStorage` value (survives query-stripping navigations)
 * 4. `REACT_APP_TEST_EMAIL` environment variable
 * 5. Default according to `STANDALONE_DEFAULT_TEST_EMAIL` constant
 */
function getTestEmail(email) {
    if (email) {
        return email;
    }

    const activeEmail = resolveActiveTestEmail();
    if (activeEmail) {
        return activeEmail;
    }

    // Fall back to env var or default
    return process.env.REACT_APP_TEST_EMAIL || STANDALONE_DEFAULT_TEST_EMAIL;
}

/**
 * Get boolean query parameter value with proper fallback
 *
 * @private
 * @param {string} paramName - Query parameter name (e.g., 'force-upgrade')
 * @param {boolean} defaultValue - Default value if parameter not found
 * @returns {boolean} Boolean value from query param or default
 *
 * Supports truthy values: 'true', '1'
 * Supports falsy values: 'false', '0'
 * Any other value returns false
 */
function getBooleanQueryParam(paramName, defaultValue) {
    // Check URL query parameters
    if (typeof window !== 'undefined' && window.location) {
        const urlParams = new URLSearchParams(window.location.search);
        const queryValue = urlParams.get(paramName);

        if (queryValue !== null) {
            // Use envToBoolean for consistent boolean parsing
            return envToBoolean(queryValue, false);
        }
    }

    return defaultValue;
}

/**
 * Base64 URL encode a string
 *
 * Note: This function uses btoa() which is a browser-only API. This is intentional
 * as it's only called in standalone mode (browser context).
 *
 * @private
 * @param {string} str - String to encode
 * @returns {string} Base64 URL encoded string
 */
function base64UrlEncode(str) {
    const base64 = btoa(str);
    // Convert base64 to base64url by replacing +/= with -_
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Generate a JWT token for standalone mode testing
 * Note: This is for development/testing only. The signature is not cryptographically secure
 * but that's okay because Daisy's localhost exception validates by email lookup, not signature.
 *
 * @private
 * @param {string} email - Email address for the test user
 * @returns {string} A JWT token with the specified email
 */
function generateTestJWT(email) {
    const header = {
        typ: 'JWT',
        alg: 'HS256',
        kid: 'test-key',
    };

    const now = Math.floor(Date.now() / 1000);
    const payload = {
        sub: email,
        iss: 'http://localhost:8889',
        role: 'Owner',
        iat: now,
        exp: now + 60 * 60 * 24 * 365, // Expires in 1 year
    };

    const headerEncoded = base64UrlEncode(JSON.stringify(header));
    const payloadEncoded = base64UrlEncode(JSON.stringify(payload));

    // For localhost mode, signature is not verified but we need some value
    return `${headerEncoded}.${payloadEncoded}.test-signature`;
}

/**
 * Returns a mock JWT token for standalone mode testing
 *
 * @param {string} [email] - Email address for the test user. If not provided, checks
 *                           URL query parameter ?test-email=..., then REACT_APP_TEST_EMAIL
 *                           env var, or defaults to STANDALONE_DEFAULT_TEST_EMAIL constant
 * @returns {string} A test JWT token with the specified email
 */
export function getTestJWT(email) {
    const testEmail = getTestEmail(email);
    return generateTestJWT(testEmail);
}

/**
 * Returns mock forceUpgradeInfo response for standalone mode testing and development
 * This matches the structure that Ghost Admin sends via postMessage
 *
 * @param {string} [email] - Email address for the owner user. If not provided, checks query param or uses default
 * @param {string} [name] - Name for the owner user. If not provided, uses 'Test User'
 * @param {boolean} [forceUpgrade] - Whether to simulate force upgrade state. If not provided, checks ?force-upgrade=... query param or defaults to false
 * @param {boolean} [isOwner] - Whether the user is the owner. If not provided, checks ?is-owner=... query param or defaults to true
 * @returns {object} Force upgrade info object with forceUpgrade, isOwner, and ownerUser properties
 *
 * Priority chain for forceUpgrade and isOwner:
 * 1. Function parameter
 * 2. URL query parameter (?force-upgrade=..., ?is-owner=...)
 * 3. Default values (false for forceUpgrade, true for isOwner)
 */
export function getStandaloneForceUpgradeResponse(email, name, forceUpgrade, isOwner) {
    const ownerEmail = getTestEmail(email);
    const ownerName = name || 'Test User';

    // Check query params if parameters not explicitly provided
    // Note: Using !== undefined allows explicit false to override query params
    // e.g., getStandaloneForceUpgradeResponse(undefined, undefined, false) uses false, not query param
    const finalForceUpgrade =
        forceUpgrade !== undefined ? forceUpgrade : getBooleanQueryParam('force-upgrade', false);

    const finalIsOwner = isOwner !== undefined ? isOwner : getBooleanQueryParam('is-owner', true);

    return {
        forceUpgrade: finalForceUpgrade,
        isOwner: finalIsOwner,
        ownerUser: {
            name: ownerName,
            email: ownerEmail,
        },
    };
}
